import React, { useLayoutEffect, useEffect } from 'react';
import { Control } from '@yleisradio/areena-types';
import styles from './ContextMenu.module.scss';
import { useContextMenuNavigation } from './useContextMenuNavigation';
import { blur, triggerFocus } from '../navigation/utils';
import { useContextMenu } from './useContextMenu';
import { isNavigator, isOperator } from '../typeGuards';
import ContextMenuButton from './ContextMenuButton';
import { logger } from '../../common/logger';
import { useNavigateWithHistory } from '../Routes/useNavigateWithHistory';
import getRoutePath from '../Routes/getRoutePath';
import { getItemIdFromOperator } from '../utils/item';
import { useContextMenuContext } from '../contexts/ContextMenuContext/ContextMenuContext';
import { useInputMethodContext } from '../contexts/InputMethodContext/InputMethodContext';
import { useEventListener } from '../hooks/useEventListener';
import { Keys } from '../utils/keys';
import { useAriaLive } from '../contexts/AriaLiveContext/AriaLiveContext';

const ContextMenu: React.FC = () => {
  const navigate = useNavigateWithHistory();

  const {
    contextMenuSource,
    isContextMenuOpen: isOpen,
    closeContextMenu: close,
    title,
    info,
    labels,
  } = useContextMenuContext();
  const { data } = useContextMenu(contextMenuSource);
  const { focus, triggerNavigation } = useContextMenuNavigation(
    data.concat({ type: 'fake' } as Control) // Adding a fake control to ensure the last button is always focusable
  );
  const { inputMethod } = useInputMethodContext();
  const isInputMethodKeyboard = inputMethod === 'keyboard';
  const { announceAssertive } = useAriaLive();

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => announceAssertive('Kontekstivalikko avattu'), 1000); // TODO translation
  }, [isOpen, announceAssertive]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (isInputMethodKeyboard) {
      triggerFocus(focus, true);
    } else {
      blur(focus);
    }
  }, [focus, isInputMethodKeyboard, isOpen]);

  const onKeyDownEvent = (e: any) => {
    if (!isOpen || !isInputMethodKeyboard) return;

    if (
      (e.keyCode === Keys.VK_SPACE || e.keyCode === Keys.VK_ENTER) &&
      e.repeat
    ) {
      e.preventDefault();
      e.stopPropagation();
    }

    let isEventCaptured = true;

    if (e.keyCode === Keys.VK_UP || e.keyCode === Keys.VK_DOWN) {
      triggerNavigation(e);
    } else if ([Keys.VK_BACK, Keys.VK_BACKSPACE].includes(e.keyCode)) {
      close();
    } else {
      isEventCaptured = false;
    }

    if (isEventCaptured) {
      e.preventDefault();
    }
  };

  useEventListener('keydown', onKeyDownEvent, document);

  const getButton = (control: Control, index: number) => {
    switch (true) {
      case isNavigator(control):
        const routePath = getRoutePath({
          pointer: control.destination,
          functionality: control.functionality,
        });

        const handleNavigatorOnClick = (
          e: React.MouseEvent | React.KeyboardEvent
        ) => {
          if (
            e instanceof KeyboardEvent &&
            !e.repeat &&
            (e.keyCode == Keys.VK_SPACE || e.keyCode == Keys.VK_ENTER)
          ) {
            navigate(routePath);
            close();
          }

          if (e instanceof MouseEvent) {
            e.preventDefault();
            e.stopPropagation();
            navigate(routePath);
            close();
          }
        };

        return (
          <ContextMenuButton
            id={`0/-4/${index}`}
            onClick={handleNavigatorOnClick}
            onKeyDown={handleNavigatorOnClick}
            key={'NavigatorControl' + index}
            icon={control.icon}
          >
            {control.title}
          </ContextMenuButton>
        );
      case isOperator(control):
        const itemId = getItemIdFromOperator(control);
        if (!itemId) {
          logger.error('Operator is missing item id:', control);
          return null;
        }

        return (
          <ContextMenuButton
            id={`0/-4/${index}`}
            onClick={() => {
              close();
            }}
            icon={control.icon}
            key={'OperatorControl' + index}
          >
            {control.title}
          </ContextMenuButton>
        );
      default:
        logger.error('Unknown control type in context menu:', control);
        return null;
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    if (
      !e.repeat &&
      (e.keyCode === Keys.VK_SPACE || e.keyCode === Keys.VK_ENTER)
    ) {
      e.stopPropagation();
      e.preventDefault();
      close();
    }
  };

  return (
    <div className={styles.contextMenu__overlay} onClick={close}>
      <div
        className={styles.contextMenu}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className={styles.contextMenu__header}>
          <h3 className={styles.contextMenu__title}>{title}</h3>
          <p className={styles.contextMenu__info}>{info}</p>
          <p className={styles.contextMenu__labels}>{labels}</p>
        </div>
        <div className={styles.contextMenu__buttons}>
          {data.map((control, index) => getButton(control, index))}
          <ContextMenuButton
            onClick={() => {
              close();
            }}
            onKeyDown={handleOnKeyDown}
            key={'contextMenuClose'}
            id={`0/-4/${data.length}`}
            icon={{ id: 'cancel', version: 'x' }}
          >
            Peruuta
          </ContextMenuButton>
        </div>
      </div>
    </div>
  );
};

export default ContextMenu;
